import { ChangeEvent, useCallback } from 'react';
import { LocationResponseDto } from '@tiendanube/common';
import { ShippingRestrictionScope } from '@tiendanube/common/src/enums';
import { Stack } from 'commons/components';
import { useModal } from 'commons/hooks';
import { useLocations } from 'domains/Shipping/Locations/hooks';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import { useAutoResetSelection } from '../../../../ShippingRestrictionPages/hooks';
import OptionsSection from '../../../OptionsSection';
import SelectedItemsChipList from '../../../SelectedItemsChipList';
import { GenericSelectionModal } from '../../../SelectionModals';
import LocationCheckboxItem from '../../../SelectionModals/LocationsSelectionModal/LocationCheckboxItem';
import { RadioOption } from '../../../shared/types';

interface DistributionCenterSectionProps {
  distributionCenter?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  specificDistributionCenters: string[];
  onDistributionCentersChange: (centers: string[]) => void;
}

function DistributionCenterSection({
  distributionCenter = ShippingRestrictionScope.ALL,
  onChange,
  specificDistributionCenters,
  onDistributionCentersChange,
}: Readonly<DistributionCenterSectionProps>): JSX.Element {
  const { locations } = useLocations();

  const [showModal, openModal, closeModal] = useModal();

  const t = useTranslationShipping(
    'deliveryMethods.shippingLimiting.availability',
  );

  const distributionCenterOptions: RadioOption[] = [
    {
      label: t('distributionCenter.all'),
      value: ShippingRestrictionScope.ALL,
    },
    {
      label: t('distributionCenter.specific'),
      value: ShippingRestrictionScope.SPECIFIC,
      disabled: locations.length <= 1,
    },
  ];

  const isDisabled = useCallback((location: LocationResponseDto): boolean => {
    if (!location.tags || location.tags.length === 0) return false;
    return (
      location.tags.includes('offline') && !location.tags.includes('online')
    );
  }, []);

  const getDisabledMessage = useCallback(
    (location: LocationResponseDto): string | undefined => {
      if (isDisabled(location)) {
        return t('distributionCenter.disabledMessage');
      }
      return undefined;
    },
    [isDisabled, t],
  );

  const {
    handleSubmit: handleAutoResetSubmit,
    handleRemove: handleAutoResetRemove,
    handleClose: handleAutoResetClose,
  } = useAutoResetSelection({
    fieldName: 'distributionCenterScope',
    resetValue: ShippingRestrictionScope.ALL,
    onChange,
    onItemsChange: onDistributionCentersChange,
    selectedItems: specificDistributionCenters,
    allItems: locations,
    getItemId: (location) => location.id,
  });

  const handleChangeDistributionCenter = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange(e);

      if (
        e.target.value === ShippingRestrictionScope.SPECIFIC &&
        specificDistributionCenters.length === 0
      ) {
        openModal();
      }
    },
    [onChange, specificDistributionCenters.length, openModal],
  );

  const handleSubmit = useCallback(
    (selectedCenters: string[]) => {
      handleAutoResetSubmit(selectedCenters);
      closeModal();
    },
    [handleAutoResetSubmit, closeModal],
  );

  const handleCloseModal = useCallback(() => {
    handleAutoResetClose();
    closeModal();
  }, [handleAutoResetClose, closeModal]);

  const getLocationName = (id: string | number): string => {
    const location = locations.find((l) => l.id === id);
    return location?.name || id.toString();
  };

  return (
    <>
      <Stack column align="stretch">
        <Stack.Item>
          <OptionsSection
            title={t('distributionCenter.title')}
            name="distributionCenterScope"
            options={distributionCenterOptions}
            value={distributionCenter}
            onChange={handleChangeDistributionCenter}
          />
        </Stack.Item>
        {distributionCenter === ShippingRestrictionScope.SPECIFIC &&
          specificDistributionCenters.length > 0 && (
            <Stack.Item>
              <SelectedItemsChipList
                items={specificDistributionCenters}
                editLinkText={t('distributionCenter.editLink')}
                getItemText={getLocationName}
                onEdit={openModal}
                onRemove={handleAutoResetRemove}
              />
            </Stack.Item>
          )}
      </Stack>

      {showModal && (
        <GenericSelectionModal<LocationResponseDto>
          isOpen
          onClose={handleCloseModal}
          onSubmit={handleSubmit}
          preselected={specificDistributionCenters}
          items={locations}
          getItemId={(location) => location.id}
          getItemSearchText={(location) => location.name}
          renderItem={(location, isSelected, onToggle, isDisabled) => (
            <LocationCheckboxItem
              key={location.id}
              location={location}
              checked={isSelected}
              onChange={onToggle}
              disabled={isDisabled}
              disabledMessage={getDisabledMessage(location)}
            />
          )}
          translationPath="deliveryMethods.shippingLimiting.modals.locations"
          showSelectAll={false}
          getItemDisabled={isDisabled}
          getItemDisabledMessage={getDisabledMessage}
        />
      )}
    </>
  );
}

export default DistributionCenterSection;
